import React, { useState } from 'react';
import Modal from './Modal';
import { goalsApi } from '../api/client';

interface AddGoalModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGoalAdded: () => void;
}

const AddGoalModal: React.FC<AddGoalModalProps> = ({ isOpen, onClose, onGoalAdded }) => {
  const [formData, setFormData] = useState({
    goal: '',
    type: 'shortTerm' as 'shortTerm' | 'longTerm',
    priority: 'medium' as 'high' | 'medium' | 'low',
    deadline: '',
    timeframe: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const resetForm = () => {
    setFormData({
      goal: '',
      type: 'shortTerm',
      priority: 'medium',
      deadline: '',
      timeframe: ''
    });
    setError(null);
    setSuccess(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.goal.trim()) {
      setError('Goal text is required');
      return;
    }

    if (formData.type === 'shortTerm' && !formData.deadline) {
      setError('Deadline is required for short-term goals');
      return;
    }

    if (formData.type === 'longTerm' && !formData.timeframe) {
      setError('Timeframe is required for long-term goals');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await goalsApi.addGoal({
        type: formData.type,
        goal: formData.goal,
        priority: formData.priority,
        ...(formData.type === 'shortTerm' && { deadline: formData.deadline }),
        ...(formData.type === 'longTerm' && { timeframe: formData.timeframe })
      });

      setSuccess(true);
      setTimeout(() => {
        onGoalAdded();
        handleClose();
      }, 1500);
    } catch (err) {
      console.error('Error adding goal:', err);
      setError('Failed to add goal. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Add New Goal">
      <form onSubmit={handleSubmit} className="modal-form">
        {error && (
          <div className="modal-error">
            {error}
          </div>
        )}

        {success && (
          <div className="modal-success">
            Goal added successfully!
          </div>
        )}

        <div className="modal-form-group">
          <label>Goal Type</label>
          <select
            value={formData.type}
            onChange={(e) => setFormData({ ...formData, type: e.target.value as 'shortTerm' | 'longTerm' })}
            disabled={loading}
          >
            <option value="shortTerm">Short-term Goal</option>
            <option value="longTerm">Long-term Goal</option>
          </select>
        </div>

        <div className="modal-form-group">
          <label>Goal Description</label>
          <textarea
            value={formData.goal}
            onChange={(e) => setFormData({ ...formData, goal: e.target.value })}
            placeholder="Describe your goal..."
            disabled={loading}
            rows={3}
          />
        </div>

        <div className="modal-form-row">
          <div className="modal-form-group">
            <label>Priority</label>
            <select
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value as 'high' | 'medium' | 'low' })}
              disabled={loading}
            >
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>

          {formData.type === 'shortTerm' && (
            <div className="modal-form-group">
              <label>Deadline</label>
              <input
                type="date"
                value={formData.deadline}
                onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                disabled={loading}
              />
            </div>
          )}

          {formData.type === 'longTerm' && (
            <div className="modal-form-group">
              <label>Timeframe</label>
              <input
                type="text"
                value={formData.timeframe}
                onChange={(e) => setFormData({ ...formData, timeframe: e.target.value })}
                placeholder="e.g., Next 2 years"
                disabled={loading}
              />
            </div>
          )}
        </div>

        <div className="modal-form-actions">
          <button
            type="button"
            onClick={handleClose}
            className="modal-btn modal-btn-cancel"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="modal-btn modal-btn-primary"
            disabled={loading}
          >
            {loading ? 'Adding...' : 'Add Goal'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default AddGoalModal;